import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState } from 'react';
import './OnboardingWorkflow.css';

/**
 * Field types available in the form builder
 */
const FIELD_TYPES = [
    { id: 'text', label: 'Text', icon: 'fa-font' },
    { id: 'email', label: 'Email', icon: 'fa-envelope' },
    { id: 'phone', label: 'Phone', icon: 'fa-phone' },
    { id: 'date', label: 'Date', icon: 'fa-calendar' },
    { id: 'select', label: 'Dropdown', icon: 'fa-chevron-down' },
    { id: 'checkbox', label: 'Checkbox', icon: 'fa-square-check' },
    { id: 'textarea', label: 'Long Text', icon: 'fa-align-left' },
    { id: 'file', label: 'File Upload', icon: 'fa-upload' }
];

/**
 * Individual field editor component
 */
const FormFieldEditor = ({ field, onChange, onRemove, index }) => {
  useUiLanguage();
    const [isExpanded, setIsExpanded] = useState(true);

    const handleOptionAdd = () => {
        const newOptions = [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`];
        onChange({ ...field, options: newOptions });
    };

    const handleOptionChange = (optIndex, value) => {
        const newOptions = [...(field.options || [])];
        newOptions[optIndex] = value;
        onChange({ ...field, options: newOptions });
    };

    const handleOptionRemove = (optIndex) => {
        const newOptions = (field.options || []).filter((_, i) => i !== optIndex);
        onChange({ ...field, options: newOptions });
    };

    const fieldType = FIELD_TYPES.find(t => t.id === field.type);

    return (
        <div className="form-field-editor">
            <div className="field-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="field-drag-handle">
                    <i className="fa-solid fa-grip-vertical" />
                </div>
                <div className="field-type-badge">
                    <i className={`fa-solid ${fieldType?.icon || 'fa-font'}`} />
                    {fieldType?.label || uiText("Field")}
                </div>
                <span className="field-label-preview">
                    {field.label || uiText("Untitled Field")}
                </span>
                {field.required && (
                    <span className="required-badge">{uiText("Required")}</span>
                )}
                <button
                    type="button"
                    className="field-remove-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                >
                    <i className="fa-solid fa-trash" />
                </button>
                <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} field-toggle`} />
            </div>

            {isExpanded && (
                <div className="field-body">
                    <div className="form-row">
                        <div className="form-group">
                            <label>{uiText("Field Label")}</label>
                            <input
                                type="text"
                                value={field.label || ''}
                                onChange={e => onChange({ ...field, label: e.target.value })}
                                placeholder={uiText("e.g., Emergency Contact Name")}
                            />
                        </div>
                        <div className="form-group">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={field.required || false}
                                    onChange={e => onChange({ ...field, required: e.target.checked })}
                                />
                                <span>{uiText("Required Field")}</span>
                            </label>
                        </div>
                    </div>

                    {/* Placeholder for text-based fields */}
                    {['text', 'email', 'phone', 'textarea'].includes(field.type) && (
                        <div className="form-group">
                            <label>{uiText("Placeholder Text")}</label>
                            <input
                                type="text"
                                value={field.placeholder || ''}
                                onChange={e => onChange({ ...field, placeholder: e.target.value })}
                                placeholder={uiText("Hint text...")}
                            />
                        </div>
                    )}

                    {/* Options for dropdown */}
                    {field.type === 'select' && (
                        <div className="form-group">
                            <label>{uiText("Options")}</label>
                            <div className="options-list">
                                {(field.options || []).map((option, optIdx) => (
                                    <div key={optIdx} className="option-row">
                                        <input
                                            type="text"
                                            value={option}
                                            onChange={e => handleOptionChange(optIdx, e.target.value)}
                                            placeholder={`Option ${optIdx + 1}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleOptionRemove(optIdx)}
                                            className="option-remove"
                                        >
                                            <i className="fa-solid fa-times" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="btn-add-option"
                                    onClick={handleOptionAdd}
                                >
                                    <i className="fa-solid fa-plus" />{uiText("Add Option")}</button>
                            </div>
                        </div>
                    )}

                    {/* Checkbox label */}
                    {field.type === 'checkbox' && (
                        <div className="form-group">
                            <label>{uiText("Checkbox Text")}</label>
                            <input
                                type="text"
                                value={field.checkboxLabel || ''}
                                onChange={e => onChange({ ...field, checkboxLabel: e.target.value })}
                                placeholder={uiText("I agree to the terms...")}
                            />
                        </div>
                    )}

                    {/* File upload settings */}
                    {field.type === 'file' && (
                        <div className="form-group">
                            <label>{uiText("Accepted File Types")}</label>
                            <input
                                type="text"
                                value={field.acceptedTypes || ''}
                                onChange={e => onChange({ ...field, acceptedTypes: e.target.value })}
                                placeholder={uiText(".pdf, .doc, .jpg (leave empty for all)")}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * Modal for building custom forms for the "Send Form" automation
 */
const FormBuilderModal = ({
    onClose,
    onSave,
    initialConfig = null
}) => {
  useUiLanguage();
    const [formTitle, setFormTitle] = useState(initialConfig?.title || '');
    const [formDescription, setFormDescription] = useState(initialConfig?.description || '');
    const [fields, setFields] = useState(initialConfig?.fields || []);

    const addField = (type) => {
        const fieldType = FIELD_TYPES.find(t => t.id === type);
        setFields([...fields, {
            id: `field_${Date.now()}`,
            type,
            label: '',
            required: false,
            placeholder: '',
            options: type === 'select' ? ['Option 1', 'Option 2'] : undefined
        }]);
    };

    const updateField = (index, updatedField) => {
        const newFields = [...fields];
        newFields[index] = updatedField;
        setFields(newFields);
    };

    const removeField = (index) => {
        setFields(fields.filter((_, i) => i !== index));
    };

    const moveField = (fromIndex, toIndex) => {
        const newFields = [...fields];
        const [removed] = newFields.splice(fromIndex, 1);
        newFields.splice(toIndex, 0, removed);
        setFields(newFields);
    };

    const handleSave = () => {
        if (!formTitle.trim()) {
            alert(uiText('Please enter a form title'));
            return;
        }

        if (fields.length === 0) {
            alert(uiText('Please add at least one field'));
            return;
        }

        // Validate all fields have labels
        const invalidFields = fields.filter(f => !f.label?.trim());
        if (invalidFields.length > 0) {
            alert(uiText('Please add labels to all fields'));
            return;
        }

        onSave({
            title: formTitle.trim(),
            description: formDescription.trim(),
            fields
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content-onboarding form-builder-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{uiText("Create Information Form")}</h2>
                    <button className="close" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body-onboarding">
                    {/* Form Title & Description */}
                    <div className="form-group">
                        <label>{uiText("Form Title *")}</label>
                        <input
                            type="text"
                            value={formTitle}
                            onChange={e => setFormTitle(e.target.value)}
                            placeholder={uiText("e.g., Emergency Contact Information")}
                            className="form-title-input"
                        />
                    </div>

                    <div className="form-group">
                        <label>{uiText("Form Description")}</label>
                        <textarea
                            value={formDescription}
                            onChange={e => setFormDescription(e.target.value)}
                            placeholder={uiText("Instructions for the new hire...")}
                            rows={2}
                        />
                    </div>

                    {/* Field Type Buttons */}
                    <div className="add-field-section">
                        <label>{uiText("Add Field")}</label>
                        <div className="field-type-buttons">
                            {FIELD_TYPES.map(type => (
                                <button
                                    key={type.id}
                                    type="button"
                                    className="field-type-btn"
                                    onClick={() => addField(type.id)}
                                >
                                    <i className={`fa-solid ${type.icon}`} />
                                    <span>{uiText(type.label)}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Fields List */}
                    <div className="fields-list">
                        {fields.length === 0 ? (
                            <div className="fields-empty">
                                <i className="fa-solid fa-list" />
                                <p>{uiText("No fields added yet")}</p>
                                <span>{uiText("Click a field type above to add fields to your form")}</span>
                            </div>
                        ) : (
                            fields.map((field, idx) => (
                                <FormFieldEditor
                                    key={field.id}
                                    field={field}
                                    index={idx}
                                    onChange={updated => updateField(idx, updated)}
                                    onRemove={() => removeField(idx)}
                                />
                            ))
                        )}
                    </div>

                    {/* Form Preview Info */}
                    {fields.length > 0 && (
                        <div className="form-preview-info">
                            <i className="fa-solid fa-info-circle" />
                            <span>{uiText("This form will be sent to the new hire to collect information. Responses can be exported as CSV.")}</span>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={onClose}>{uiText("Cancel")}</button>
                    <button type="button" className="btn-primary" onClick={handleSave}>{uiText("Save Form")}</button>
                </div>
            </div>
        </div>
    );
};

export default FormBuilderModal;
export { FIELD_TYPES, FormFieldEditor };
